import type { Locale } from '@/lib/i18n/config'
import { localizedGroupLabel } from './localizedName'
import type { GuidedExtrasStep, GuidedModifierGroup } from './guidedSteps'

/**
 * Título del paso de extras del flujo guiado del kiosko.
 *
 * Devuelve el rótulo del grupo (`localizedGroupLabel`, la misma regla que el
 * modal clásico) SOLO cuando todas las opciones del paso — add, remove y
 * choiceGroups — vienen de un único grupo y el dueño le escribió
 * `display_name`. En cualquier otro caso devuelve `null`, y la vista usa el
 * «¿Algo más?» de siempre.
 *
 * Por qué no basta con `localizedGroupLabel`: sin `display_name` cae al nombre
 * interno («Factory Toppings», rotulado «Solo visible en admin»), y un local
 * que nunca escribió `display_name` tiene que ver exactamente lo de hoy.
 *
 * `groups` son los grupos del ítem (de `item_modifier_groups`): las opciones
 * de add/remove solo traen `groupId`, no el grupo.
 */
export function guidedExtrasTitle(
  step: GuidedExtrasStep,
  groups: GuidedModifierGroup[],
  locale: Locale,
): string | null {
  const ids = new Set<string>([
    ...step.add.map((o) => o.groupId),
    ...step.remove.map((o) => o.groupId),
    ...step.choiceGroups.map((c) => c.group.id),
  ])
  if (ids.size !== 1) return null
  const [id] = ids
  const group = groups.find((g) => g.id === id)
  if (!group || !ownerWroteDisplayName(group)) return null
  return localizedGroupLabel(group, locale)
}

/**
 * En prod `display_name` nunca llega vacío: el formulario lo rellena con el
 * nombre (2026-10-19: 375 de 375 grupos, 64 idénticos al `name`). Uno igual al
 * nombre interno no es un texto escrito para el cliente — «HIELO», «EXTRA»,
 * «SIN» —, así que no reemplaza a «¿Algo más?». Mayúsculas y espacios no
 * cuentan: «SALSAS» → «Salsas» es el mismo rótulo.
 */
function ownerWroteDisplayName(group: GuidedModifierGroup): boolean {
  const display = group.display_name?.trim().toLocaleLowerCase()
  return !!display && display !== group.name.trim().toLocaleLowerCase()
}
